import type { FileUploadStatus } from '../../hooks/use-file-upload';

interface Props {
  file: File;
  status: FileUploadStatus;
  progress: number;
  error?: string | null;
  onRemove: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileChip({ file, status, progress, error, onRemove }: Props) {
  const isError = status === 'error';
  const isUploading = status === 'uploading';
  const color = isError ? 'var(--kind-failure)' : 'var(--text)';
  const border = isError ? 'var(--kind-failure)' : 'var(--border-strong)';

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 6px 4px 10px',
        background: 'var(--bg-alt)',
        border: `1px solid ${border}`,
        borderRadius: 6,
        fontSize: 12,
        color,
        maxWidth: 240,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {isUploading && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            height: 2,
            width: `${progress}%`,
            background: 'var(--accent)',
            transition: 'width 120ms ease',
          }}
        />
      )}
      <span aria-hidden>📎</span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: 500,
        }}
        title={error ?? file.name}
      >
        {file.name}
      </span>
      <span style={{ fontSize: 10, color: 'var(--text-mute)', whiteSpace: 'nowrap' }}>
        {isUploading ? `${progress}%` : isError ? 'error' : formatBytes(file.size)}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${file.name}`}
        style={{
          width: 18,
          height: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 0,
          background: 'transparent',
          color: 'var(--text-soft)',
          cursor: 'pointer',
          borderRadius: 3,
          padding: 0,
          fontSize: 13,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}
